import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useTeamStore } from '@/stores/teamStore';
import { useUIStore } from '@/stores/uiStore';
import { BRANDING } from '@/lib/branding';
import { resolveHeaderLogoUrl } from '@/utils/headerLogo';
import * as notificationsApi from '@/api/notifications';
import { NotificationsModal } from './NotificationsModal';

function LogoWithSkeleton({ src, alt }: { src: string; alt: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      {!loaded && (
        <div
          className="header-logo-skeleton"
          style={{
            width: '120px',
            height: '28px',
            borderRadius: '4px',
            background: 'var(--bg-secondary)',
            animation: 'skeletonPulse 1.5s ease-in-out infinite',
          }}
        />
      )}
      <img
        src={src}
        alt={alt}
        className="header-logo-img"
        onLoad={() => setLoaded(true)}
        style={loaded ? undefined : { position: 'absolute', opacity: 0 }}
      />
      <style>{`
        @keyframes skeletonPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </>
  );
}

interface HeaderProps {
  lastUpdated?: Date | null;
}

export function Header({ lastUpdated }: HeaderProps) {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const currentTeam = useTeamStore((state) => state.currentTeam);
  const userMenuOpen = useUIStore((state) => state.userMenuOpen);
  const toggleUserMenu = useUIStore((state) => state.toggleUserMenu);
  const closeUserMenu = useUIStore((state) => state.closeUserMenu);
  const themeMode = useUIStore((state) => state.themeMode);
  const cycleTheme = useUIStore((state) => state.cycleTheme);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [hasUnseenNotifications, setHasUnseenNotifications] = useState(false);

  useEffect(() => {
    if (!currentTeam?.id || !user?.id) {
      setHasUnseenNotifications(false);
      return;
    }

    let cancelled = false;
    const refreshNotificationsState = async () => {
      try {
        const response = await notificationsApi.listTeamNotifications(currentTeam.id);
        if (!cancelled) setHasUnseenNotifications(response.has_unseen);
      } catch {
        if (!cancelled) setHasUnseenNotifications(false);
      }
    };

    void refreshNotificationsState();
    const intervalId = window.setInterval(refreshNotificationsState, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentTeam?.id, user?.id]);

  const handleSettingsClick = () => {
    closeUserMenu();
    navigate('/settings');
  };

  const handleLogout = () => {
    closeUserMenu();
    logout();
  };

  const handleNotificationsClick = () => {
    closeUserMenu();
    setNotificationsOpen(true);
  };

  const formatLastUpdated = (date: Date): string => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return 'Updated just now';
    if (diff < 3600) return `Updated ${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `Updated ${Math.floor(diff / 3600)}h ago`;
    return `Updated ${date.toLocaleDateString()}`;
  };

  const headerLogoSrc = resolveHeaderLogoUrl(currentTeam?.logo_url ?? null);
  const headerLogoAlt = headerLogoSrc === currentTeam?.logo_url?.trim()
    ? currentTeam?.name || 'Team'
    : BRANDING.appName;

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <LogoWithSkeleton src={headerLogoSrc} alt={headerLogoAlt} />
        </div>

        <div className="header-center">
          <div className="header-app-title" aria-label={BRANDING.appName}>
            {BRANDING.appName}
          </div>
        </div>

        <div className="header-right">
          {lastUpdated && (
            <div
              id="lastUpdatedStatus"
              className="last-updated-status"
            >
              {formatLastUpdated(lastUpdated)}
            </div>
          )}

          <button
            className={`header-icon-button notification-trigger ${hasUnseenNotifications ? 'notification-trigger--unseen' : ''}`}
            onClick={handleNotificationsClick}
            title={hasUnseenNotifications ? 'Unread notifications' : 'Notifications'}
            aria-label={hasUnseenNotifications ? 'Open unread notifications' : 'Open notifications'}
            type="button"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 8 3 8H3s3-1 3-8" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
            {hasUnseenNotifications && (
              <span className="notification-trigger__dot" aria-hidden="true" />
            )}
          </button>

          <button
            className="theme-toggle header-icon-button"
            onClick={cycleTheme}
            title={`Theme: ${themeMode}`}
            aria-label={`Current theme: ${themeMode}. Click to cycle.`}
            type="button"
          >
            {themeMode === 'light' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            )}
            {themeMode === 'dark' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
            {themeMode === 'system' && (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            )}
          </button>

          <div className="user-menu-container">
            <div
              className="user-info"
              onClick={toggleUserMenu}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleUserMenu()}
            >
              <div className="user-avatar" id="userAvatar">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name ?? 'User avatar'}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span>{user?.name?.charAt(0) ?? user?.email?.charAt(0) ?? '?'}</span>
                )}
              </div>
              <span id="userName">{user?.name ?? user?.email}</span>
              <svg
                className="dropdown-arrow"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 10L12 15L17 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div id="userDropdown" className={`user-dropdown-menu ${userMenuOpen ? 'active' : ''}`}>
              <button className="dropdown-item" onClick={handleSettingsClick}>
                Settings
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={handleLogout}>
                🚪 Log Out
              </button>
            </div>
          </div>
        </div>
      </div>
      <NotificationsModal
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        teamId={currentTeam?.id}
        currentUserEmail={user?.email}
        onUnseenChange={setHasUnseenNotifications}
      />
    </header>
  );
}

export default Header;
